import React, { useState } from "react";
import dayjs from "dayjs";
import { ThemeProvider } from "@mui/system";
import { Tooltip, IconButton, Box, Chip } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { useDispatch } from "react-redux";

import * as DefaultRoutes from "@Routes/DefaultRoutes";
import GenericRnDListing from "../../RnDMember/GenericRnDListing/GenericRnDListing";
import { GET_EXIT_REQUEST, MY_RD_EXIT_REQUESTS_EXPORT } from "@API/APIRoutes";
import { setData } from "@Redux/requestDataSlice";
import {
  stringCustomOperators,
  numberCustomOperators,
} from "@Utils/Validations/HelperFunctions/CustomFilters";
import { getStatusColor } from "../../../Utils/Validations/HelperFunctions/StyleFunction";

const API_ENDPOINT_MY_RD_PENDING = "/my/rd";
const PAGE_TITLE = "Coordinator Incoming Requests";
const ERROR_MESSAGE = "Failed to fetch incoming requests.";
const TOOLTIP_RESPOND = "Create Product List";
const TOOLTIP_EDIT = "Edit Product List";
const TOOLTIP_VIEW = "View Request Details";
const DEFAULT_VALUE = "-";
const ICON_SIZE = "small";

const formatDate = (dateString) =>
  dateString ? dayjs(dateString).format("MM/DD/YYYY") : DEFAULT_VALUE;

const getNestedValue = (obj, path) => {
  const value = path.split(".").reduce((acc, part) => acc?.[part], obj);
  return value || DEFAULT_VALUE;
};

function CoordinatorIncomingRequestsListing() {
  const dispatch = useDispatch();

  const defaultColumns = [
    {
      field: "id",
      headerName: "Request ID",
      flex: 1.0,
      minWidth: 80,
      type: "number",
      align: "left",
      filterOperators: numberCustomOperators,
    },
    {
      field: "ingredient",
      headerName: "Ingredient",
      flex: 1.2,
      minWidth: 150,
      type: "string",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div>
          <div>{params.row.ingredient?.name}</div>
          <div style={{ fontSize: "0.75rem", color: "gray" }}>
            CAS: {params.row.ingredient?.casNumber}
          </div>
        </div>
      ),
      filterOperators: stringCustomOperators,
    },
    {
      field: "userEmail",
      headerName: "Created By",
      flex: 1.5,
      minWidth: 160,
      valueGetter: (params) => getNestedValue(params.row, "user.email"),
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "categoryName",
      headerName: "Category",
      flex: 1.2,
      minWidth: 120,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "primaryDate",
      headerName: "Primary Date",
      flex: 1.2,
      minWidth: 120,
      valueGetter: (params) =>
        params.row.primaryDate ? new Date(params.row.primaryDate) : null,
      valueFormatter: (params) => formatDate(params.value),
      type: "date",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.5,
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          label={params.row.status?.replace(/_/g, " ")}
          color={getStatusColor(params.row.status)}
          size="small"
        />
      ),
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "hasResponded",
      headerName: "Responded",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => {
        const hasResponded = params.row.hasResponded === true;
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: hasResponded ? "success.main" : "error.main",
                color: "white",
                transform: "translateX(-16px)",
              }}
            >
              {hasResponded ? (
                <CheckCircleIcon fontSize={ICON_SIZE} />
              ) : (
                <DoDisturbIcon fontSize={ICON_SIZE} />
              )}
            </Box>
          </Box>
        );
      },
      type: "boolean",
      sortable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      disableReorder: true,
    },
  ];

  const initialColumnVisibilityModel = {
    createdAt: false,
    secondaryDate: false,
  };

  const handleRespondClick = (row, navigate) => {
    dispatch(setData(row));
    navigate(
      DefaultRoutes.rdCoordinatorResponsePage
        .replace(":reqId", row.id)
        .replace(":categoryAssignmentId", row.categoryAssignmentId)
    );
  };

  const renderActions = (params, navigate, theme) => {
    const hasResponded = params.row.hasResponded === true;
    return (
      <ThemeProvider theme={theme}>
        <Tooltip title={hasResponded ? TOOLTIP_EDIT : TOOLTIP_RESPOND}>
          <IconButton
            color={hasResponded ? "primary" : "success"}
            onClick={() => handleRespondClick(params.row, navigate)}
            size="small"
          >
            {hasResponded ? <EditIcon /> : <PlayArrowRoundedIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title={TOOLTIP_VIEW}>
          <IconButton
            color="info"
            onClick={() => {
              dispatch(setData(params.row));
              navigate(
                DefaultRoutes.rdCoordinatorRequestInfo
                  .replace(":reqId", params.row.id)
                  .replace(":categoryAssignmentId", params.row.categoryAssignmentId)
              );
            }}
            size="small"
          >
            <LightbulbOutlinedIcon />
          </IconButton>
        </Tooltip>
      </ThemeProvider>
    );
  };

  return (
    <GenericRnDListing
      title={PAGE_TITLE}
      PageIcon={<ReceiptLongIcon color="white" fontSize="large" />}
      iconColor="primary.main"
      apiRoute={`${GET_EXIT_REQUEST}${API_ENDPOINT_MY_RD_PENDING}`}
      exportApi={MY_RD_EXIT_REQUESTS_EXPORT}
      defaultColumns={defaultColumns}
      renderActions={renderActions}
      errorMessage={ERROR_MESSAGE}
      buttons={[]}
      getRowId={(row) => `${row.id}_${row.categoryAssignmentId}`}
      initialColumnVisibilityModel={initialColumnVisibilityModel}
      serverPagination={true}
    />
  );
}

export default CoordinatorIncomingRequestsListing;
